import { Box, Container, HStack, Image, Link as ChakraLink, VStack, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { UserMenu } from '../public-shell/UserMenu';
import { CreditsBadge } from '../public-shell/CreditsBadge';
import { InstallButton } from './InstallButton';
import { GlobalAudioPlayer } from '../player/GlobalAudioPlayer';
import { FullPlayer } from '../player/FullPlayer';
import { useAuth } from '../auth/AuthProvider';
import { RootState } from '../redux/store';
import { MicRecorderModal } from '../library/MicRecorderModal';
import { addTracks, setReferenceId } from '../redux/features/localAudioSlice';

// Nav entries are EITHER a Link (has `to`) OR a callback (has `action`).
// Credits moved out of the bottom nav on 2026-08-17 — the top-header
// CreditsBadge already surfaces the balance and links to /credits, so
// the tab slot got repurposed for the higher-frequency "capture a
// reference audio from mic" action.
type NavTab =
  | { to: string; label: string; icon: string; matches: RegExp }
  | { action: 'record'; label: string; icon: string; matches?: undefined };

const TABS: NavTab[] = [
  { to: '/generator',  label: 'Create',    icon: '✦', matches: /^\/generator(\/|$)/ },
  { to: '/songs',      label: 'Songs',     icon: '♫', matches: /^\/(songs|history)(\/|$)/ },
  { to: '/favourites', label: 'Faves',     icon: '♥', matches: /^\/favourites(\/|$)/ },
  { to: '/library',    label: 'Library',   icon: '❖', matches: /^\/(library|shared)(\/|$)/ },
  { action: 'record',  label: 'Record',    icon: '🎙' },
];

const NAV_HEIGHT = 68; // px, without safe-area
const HEADER_HEIGHT = 56; // px

export function AppShell({ children }: { children: React.ReactNode }) {
  const { authenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const { t } = useTranslation();
  const hasQueue = useSelector((s: RootState) => s.player.index >= 0);
  const micRecorder = useDisclosure();

  return (
    <Box
      minH="100vh"
      color="whiteAlpha.900"
      bgGradient="linear(180deg, #0F0B1F 0%, #1A0F2E 40%, #2E1240 100%)"
      position="relative"
    >
      {/* Ambient glows wrapped in a fixed, self-clipping layer.
          Putting overflow (or overflow-x) on the root Box would trap
          position:sticky (spec: any non-visible overflow on any ancestor
          makes the sticky element's containing block that ancestor,
          which pins the header to the top of the scrolled-away root
          instead of to the viewport). This wrapper is position:fixed so
          it stays viewport-relative and clips its own children. */}
      <Box
        position="fixed"
        inset={0}
        overflow="hidden"
        pointerEvents="none"
        zIndex={0}
        aria-hidden
      >
        <Box
          position="absolute" top="-160px" left="50%" transform="translateX(-50%)"
          w="560px" h="560px"
          bg="radial-gradient(circle, rgba(233,74,143,0.24) 0%, rgba(233,74,143,0) 65%)"
        />
        <Box
          position="absolute" bottom="-200px" right="-160px"
          w="520px" h="520px"
          bg="radial-gradient(circle, rgba(115,84,255,0.22) 0%, rgba(115,84,255,0) 65%)"
        />
      </Box>

      <Box
        as="header"
        position="sticky"
        top={0}
        zIndex={20}
        h={`${HEADER_HEIGHT}px`}
        bg="rgba(15, 11, 31, 0.85)"
        backdropFilter="blur(20px) saturate(1.3)"
        borderBottom="1px solid"
        borderColor="whiteAlpha.150"
      >
        <Container maxW="1120px" h="full" px={{ base: 3, md: 6 }}>
          <HStack h="full" justify="space-between" align="center">
            <Link to="/generator" aria-label={t('nav.home', 'MixGenie home')}>
              <HStack spacing={2}>
                <Image src="/logo/logo_single.png" alt="MixGenie" boxSize="30px" borderRadius="8px" />
                <Text
                  fontSize="16px"
                  fontWeight={800}
                  letterSpacing="-0.01em"
                  color="whiteAlpha.900"
                  display={{ base: 'none', sm: 'inline' }}
                >
                  MixGenie
                </Text>
              </HStack>
            </Link>
            <HStack spacing={2}>
              <InstallButton />
              {authenticated && <CreditsBadge />}
              {authenticated ? (
                <UserMenu />
              ) : (
                <ChakraLink
                  as={Link}
                  to="/login"
                  fontSize="sm"
                  color="pink.300"
                  fontWeight={600}
                  px={3}
                  py={2}
                  borderRadius="8px"
                  _hover={{ bg: 'whiteAlpha.100', textDecoration: 'none' }}
                >
                  {t('nav.signIn', 'Sign in')}
                </ChakraLink>
              )}
            </HStack>
          </HStack>
        </Container>
      </Box>

      <Box
        as="main"
        position="relative"
        zIndex={1}
        pb={{
          base: `calc(${NAV_HEIGHT + (hasQueue ? 72 : 0)}px + env(safe-area-inset-bottom, 0px) + 16px)`,
        }}
      >
        <Container maxW="1120px" px={{ base: 3, md: 6 }} py={{ base: 4, md: 6 }}>
          {children}
        </Container>
      </Box>

      <Box
        as="nav"
        position="fixed"
        left={0}
        right={0}
        bottom={0}
        zIndex={15}
        bg="rgba(10, 6, 22, 0.82)"
        backdropFilter="blur(24px) saturate(1.4)"
        borderTop="1px solid"
        borderColor="whiteAlpha.100"
        pb="env(safe-area-inset-bottom, 0px)"
        display={authenticated ? 'block' : 'none'}
        aria-label={t('nav.appNav', 'App navigation')}
      >
        <HStack
          maxW="640px"
          mx="auto"
          h={`${NAV_HEIGHT}px`}
          px={2}
          spacing={0}
          align="stretch"
          justify="space-between"
        >
          {TABS.map((tab) => {
            const isActive = 'matches' in tab && tab.matches ? tab.matches.test(location.pathname) : false;
            const key = 'to' in tab ? tab.to : `action:${tab.action}`;
            const inner = (
              <VStack
                h="full"
                justify="center"
                spacing={1}
                py={2}
                transition="all 0.15s ease"
                color={isActive ? 'pink.300' : 'whiteAlpha.700'}
                _hover={{ color: 'whiteAlpha.900' }}
                position="relative"
              >
                {isActive && (
                  <Box
                    position="absolute"
                    top={0}
                    left="50%"
                    transform="translateX(-50%)"
                    w="26px"
                    h="2px"
                    borderRadius="0 0 6px 6px"
                    bgGradient="linear(90deg, #E94A8F 0%, #7354FF 100%)"
                  />
                )}
                <Text
                  fontSize="20px"
                  lineHeight="1"
                  aria-hidden
                  style={{
                    filter: isActive
                      ? 'drop-shadow(0 0 8px rgba(233,74,143,0.55))'
                      : 'none',
                  }}
                >
                  {tab.icon}
                </Text>
                <Text
                  fontSize="10.5px"
                  fontWeight={isActive ? 700 : 500}
                  letterSpacing="0.02em"
                >
                  {tab.label}
                </Text>
              </VStack>
            );
            if ('to' in tab) {
              return (
                <Link
                  key={key}
                  to={tab.to}
                  aria-current={isActive ? 'page' : undefined}
                  data-cta={`nav:${tab.label.toLowerCase()}`}
                  style={{ flex: 1, textDecoration: 'none' }}
                >
                  {inner}
                </Link>
              );
            }
            return (
              <Box
                key={key}
                as="button"
                onClick={micRecorder.onOpen}
                data-cta={`nav:${tab.label.toLowerCase()}`}
                aria-label="Record with mic"
                style={{ flex: 1, background: 'transparent', border: 0, cursor: 'pointer' }}
              >
                {inner}
              </Box>
            );
          })}
        </HStack>
      </Box>

      <GlobalAudioPlayer />
      <FullPlayer />

      {/* Mic recorder is mounted at the shell so the bottom-nav Record
          tab can open it from any page. On confirm we dispatch the
          recorded MP3 as a local reference and navigate to /generator
          so the user immediately sees the attached chip. */}
      <MicRecorderModal
        isOpen={micRecorder.isOpen}
        onCancel={micRecorder.onClose}
        onConfirm={(file) => {
          const id = `mic-${Date.now()}`;
          dispatch(addTracks([{
            id,
            file,
            name: file.name,
            size: file.size,
            addedAt: Date.now(),
          }]));
          dispatch(setReferenceId(id));
          micRecorder.onClose();
          toast({
            title: t('nav.micAttached', 'Recording attached as reference'),
            status: 'success',
            duration: 2000,
          });
          navigate('/generator');
        }}
      />
    </Box>
  );
}
